import React from 'react';
// import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
} from '@mui/material';

import aetna from '../assets/insuranceLogos/aetna-seeklogo.svg';
import ambetter from '../assets/insuranceLogos/ambetter.png';
import anthem from '../assets/insuranceLogos/anthem-seeklogo.svg';
import bcbs from '../assets/insuranceLogos/blue-cross-blue-shield-seeklogo.png';
import humana from '../assets/insuranceLogos/Humana_logo.svg';
import magellan from '../assets/insuranceLogos/magellan-health-seeklogo.png';
import medicare from '../assets/insuranceLogos/medicare-seeklogo.svg';
import molina from '../assets/insuranceLogos/molina-healthcare-seeklogo.png';
import optum from '../assets/insuranceLogos/optum-seeklogo.svg';
import oscar from '../assets/insuranceLogos/oscar-health-seeklogo.png';
import curative from '../assets/insuranceLogos/curative-seeklogo.png';
import tricare from '../assets/insuranceLogos/tricare-seeklogo.svg';
import uhc from '../assets/insuranceLogos/unitedhealthcare-seeklogo.svg';
import wellcare from '../assets/insuranceLogos/wellcare-seeklogo.png';

const zocdocLink =
  'https://www.zocdoc.com/practice/aspire-bhp-179924?lock=true&isNewPatient=false&referrerType=widget';

const insurances = [
  { name: 'Aetna', logo: aetna },
  { name: 'Ambetter', logo: ambetter },
  { name: 'Anthem', logo: anthem },
  { name: 'Blue Cross and Blue Shield', logo: bcbs },
  { name: 'Humana', logo: humana },
  { name: 'Magellan', logo: magellan },
  { name: 'Medicare', logo: medicare },
  { name: 'Molina Healthcare', logo: molina },
  { name: 'Optum', logo: optum },
  { name: 'Oscar Health', logo: oscar },
  { name: 'Curative', logo: curative },
  { name: 'TRICARE', logo: tricare },
  { name: 'UnitedHealthcare / UHC / UBH', logo: uhc },
  { name: 'WellCare', logo: wellcare },
];

const ScheduleAndInsurances = () => {
  return (
    <>
      <Helmet>
        <title>Accepted Insurance | Aspire Behavioral Health</title>
        <meta
          name="description"
          content="Aspire Behavioral Health accepts many major insurance plans. View accepted insurance providers or call to verify your mental health coverage."
        />
      </Helmet>

      <Box
        sx={{
          backgroundColor: 'white',
          minHeight: '75vh',
          py: 8,
          px: { xs: 2, md: 6 },
        }}
      >
        <Box
          sx={{
            maxWidth: '1100px',
            mx: 'auto',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: '#3a4c17',
              fontWeight: 700,
              mb: 2,
            }}
          >
            Insurances Accepted
          </Typography>

          <Typography
            variant="body1"
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              mb: 3,
              color: 'text.secondary',
              lineHeight: 1.8,
              fontSize: { xs: '1rem', sm: '1.05rem' },
            }}
          >
            Aspire Behavioral Health accepts many major commercial insurance
            plans. Coverage can vary by provider, plan, location, and service,
            so we recommend calling our office to verify your benefits before
            your appointment.
          </Typography>

          {/* Top CTA */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
              mb: 6,
            }}
          >
            <Button
              variant="contained"
              href="tel:2108669850"
              sx={{
                backgroundColor: '#e3f3c5',
                color: 'black',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#d3eab0',
                },
              }}
            >
              Call 210-866-9850
            </Button>

            <Button
              component="a"
              href={zocdocLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              sx={{
                backgroundColor: 'transparent',
                border: '2px solid #3a4c17',
                color: '#3a4c17 !important',
                fontWeight: 600,
                px: 4,
                py: 1.3,
                borderRadius: '30px',
                textTransform: 'none',
                '&:visited': {
                  color: '#3a4c17',
                },
                '&:hover': {
                  backgroundColor: 'rgba(58,76,23,0.05)',
                  border: '2px solid #3a4c17',
                  color: '#3a4c17',
                },
              }}
            >
              Schedule an Appointment
            </Button>
          </Box>

          {/* Insurance Cards */}
          <Box
            sx={{
              backgroundColor: '#e3f3c5',
              p: { xs: 3, sm: 5 },
              borderRadius: 4,
              boxShadow: '0px 4px 12px rgba(0,0,0,0.12)',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: 'black',
                mb: 1,
              }}
            >
              Insurance Providers
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#333',
                mb: 4,
                lineHeight: 1.7,
              }}
            >
              If your insurance is not listed, please call us. We may still be
              able to verify your coverage or discuss available options.
            </Typography>

            <Grid container spacing={3} justifyContent="center">
              {insurances.map(({ name, logo }) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={name}>
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                      backgroundColor: 'white',
                      transition: '0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0px 5px 14px rgba(0,0,0,0.14)',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        minHeight: 130,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        p: 3,
                      }}
                    >
                      <Box
                        component="img"
                        src={logo}
                        alt={`${name} insurance logo`}
                        sx={{
                          height: 45,
                          width: '100%',
                          maxWidth: 120,
                          objectFit: 'contain',
                          mb: 2,
                        }}
                      />

                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#333',
                          lineHeight: 1.4,
                        }}
                      >
                        {name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Bottom CTA */}
          <Box sx={{ textAlign: 'center', mt: 7 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Have questions about your coverage?
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                href="tel:2108669850"
                sx={{
                  backgroundColor: '#e3f3c5',
                  color: 'black',
                  fontWeight: 600,
                  px: 4,
                  py: 1.3,
                  borderRadius: '30px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#d3eab0',
                  },
                }}
              >
                Call 210-866-9850
              </Button>

              <Button
                component="a"
                href={zocdocLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                sx={{
                  backgroundColor: 'transparent',
                  border: '2px solid #3a4c17',
                  color: '#3a4c17 !important',
                  fontWeight: 600,
                  px: 4,
                  py: 1.3,
                  borderRadius: '30px',
                  textTransform: 'none',
                  '&:visited': {
                    color: '#3a4c17',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(58,76,23,0.05)',
                    border: '2px solid #3a4c17',
                    color: '#3a4c17',
                  },
                }}
              >
                Schedule an Appointment
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ScheduleAndInsurances;